import React, { Fragment, useEffect, useState } from "react";
import styles from "../styles.module.scss";
import InputBulkOrder from "../InputBulkOrder";
import { Button, Form, message, Switch } from "antd";
import defaultAxios from "../../../commons/utils/axios";
import { STORE_ORDER_SETTING } from "../../../commons/constants/api-urls";
import { Loading } from "../../../commons/components/Loading";
import { useNavigate } from "react-router-dom";

interface Props {
  storeId?: string;
}

const ThresholdSetting: React.FC<Props> = ({ storeId }) => {
  const [loading, setLoading] = useState(false);
  const [isMinimumItemsAllowed, setIsMinimumItemsAllowed] = useState(false);
  const [isSellerFleetMinimumPriceAllowed, setIsSellerFleetMinimumPriceAllowed] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const onFinish = async (values: OrderSettingForm) => {
    if (!checkEmptyFields(values)) return message.error("Please fill in the enabled fields input");
    const body = {
      isEnabledMinimumQuantityBulkOrder: Boolean(values.isEnabledMinimumQuantityBulkOrder),
      isEnabledSellerFleetMinimumPrice: Boolean(values.isEnabledSellerFleetMinimumPrice),
      minimumQuantityBulkOrder: values.minimumQuantityBulkOrder ? Number(values.minimumQuantityBulkOrder) : null,
      sellerFleetMinimumPrice: values.sellerFleetMinimumPrice ? Number(values.sellerFleetMinimumPrice) : null,
    };

    setLoading(true);
    try {
      await defaultAxios.put(STORE_ORDER_SETTING(storeId!), body);
      message.success("Setting successfully");
    } catch (error) {
      console.log(error);
      message.error("some thing went wrong");
    } finally {
      setLoading(false);
    }
  };

  function handleCancel() {
    navigate(-1);
  }

  const handleSwitchChange = (checked: boolean, type: string) => {
    switch (type) {
      case "minimumQuantityBulkOrder":
        setIsMinimumItemsAllowed(checked);
        if (!checked) {
          form.setFieldsValue({ minimumQuantityBulkOrder: null });
        }
        form.setFieldsValue({ isEnabledMinimumQuantityBulkOrder: checked });
        break;
      case "sellerFleetMinimumPrice":
        setIsSellerFleetMinimumPriceAllowed(checked);
        if (!checked) {
          form.setFieldsValue({ sellerFleetMinimumPrice: null });
        }
        form.setFieldsValue({ isEnabledSellerFleetMinimumPrice: checked });
        break;
      default:
        break;
    }
  };

  const disabledFieldCheck = (type: string): boolean => {
    switch (type) {
      case "minimumQuantityBulkOrder":
        return !isMinimumItemsAllowed;
      case "sellerFleetMinimumPrice":
        return !isSellerFleetMinimumPriceAllowed;
      default:
        return false;
    }
  };

  const checkEmptyFields = (values: OrderSettingForm): boolean => {
    switch (true) {
      case values.isEnabledMinimumQuantityBulkOrder && !values.minimumQuantityBulkOrder:
      case values.isEnabledSellerFleetMinimumPrice && !values.sellerFleetMinimumPrice:
        return false;
      default:
        return true;
    }
  };

  async function handleGetOrderSetting(storeId: string) {
    let formBody: Pick<
      OrderSetting,
      | "isEnabledMinimumQuantityBulkOrder"
      | "isEnabledSellerFleetMinimumPrice"
      | "minimumQuantityBulkOrder"
      | "sellerFleetMinimumPrice"
    > = {
      isEnabledMinimumQuantityBulkOrder: false,
      isEnabledSellerFleetMinimumPrice: false,
      minimumQuantityBulkOrder: null,
      sellerFleetMinimumPrice: null,
    };
    try {
      setLoading(true);
      const { data } = await defaultAxios.get<OrderSetting>(STORE_ORDER_SETTING(storeId));
      if (data) {
        formBody = {
          isEnabledMinimumQuantityBulkOrder: Boolean(data.isEnabledMinimumQuantityBulkOrder),
          isEnabledSellerFleetMinimumPrice: Boolean(data.isEnabledSellerFleetMinimumPrice),
          minimumQuantityBulkOrder: data.minimumQuantityBulkOrder,
          sellerFleetMinimumPrice: data.sellerFleetMinimumPrice,
        };
      }
    } catch (error) {
      message.error("Cant get order setting");
      console.log(error);
    } finally {
      setLoading(false);
      form.setFieldsValue(formBody);
      setIsMinimumItemsAllowed(form.getFieldValue("isEnabledMinimumQuantityBulkOrder"));
      setIsSellerFleetMinimumPriceAllowed(form.getFieldValue("isEnabledSellerFleetMinimumPrice"));
    }
  }

  useEffect(() => {
    if (storeId) {
      handleGetOrderSetting(storeId);
    }
  }, [storeId]);

  const inputArray = [
    {
      inputName: "minimumQuantityBulkOrder",
      checkBoxName: "Minimum Items Per Order",
      message: "Minimum Item Per Order",
    },
    {
      inputName: "sellerFleetMinimumPrice",
      checkBoxName: "Seller Fleet Minimum Price",
      message: "Seller Fleet Minimum Price",
    },
  ];

  const handleToggleName = (name: string) => {
    switch (name) {
      case "minimumQuantityBulkOrder":
        return "isEnabledMinimumQuantityBulkOrder";
      case "sellerFleetMinimumPrice":
        return "isEnabledSellerFleetMinimumPrice";
      default:
        return name;
    }
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onError={e => console.log(e)}
      autoComplete="off"
      scrollToFirstError={true}
      className={styles.form}
    >
      <div className={styles.titleWrapper}>
        <div className={styles.title}>Order Setting</div>
        <div>
          {" "}
          <Button type="text" className={styles.cancel} onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Form.Item noStyle shouldUpdate={() => true}>
            {() => {
              return (
                <Button
                  id="thresholdSubmit"
                  type="primary"
                  htmlType="submit"
                  className={styles.save}
                  disabled={loading || !!form.getFieldsError().find(item => item.errors.length)}
                >
                  {loading ? "Saving" : "Save"}
                </Button>
              );
            }}
          </Form.Item>
        </div>
      </div>

      <div className={styles.titleWrapper}>
        <div className={styles.formTitle}>Bulk Order Threshold Setting</div>
      </div>
      <div className={styles.box}>
        {loading ? (
          <Loading />
        ) : (
          <Fragment>
            {inputArray.map((item, idx) => (
              <div key={idx}>
                <InputBulkOrder
                  disabled={disabledFieldCheck(item.inputName)}
                  checkBoxName={item.checkBoxName}
                  inputName={item.inputName}
                  form={form}
                  message={item.message}
                />
                <Form.Item name={handleToggleName(item.inputName)} noStyle>
                  <Switch
                    style={{ marginTop: "1rem" }}
                    defaultChecked={form.getFieldValue(handleToggleName(item.inputName))}
                    onChange={e => handleSwitchChange(e, item.inputName)}
                    checkedChildren={"Enabled"}
                    unCheckedChildren={"Disabled"}
                  />
                </Form.Item>
              </div>
            ))}
          </Fragment>
        )}
      </div>
    </Form>
  );
};

export default ThresholdSetting;
